import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  downloadCSV,
  downloadFile,
  openPdfInNewTab,
  printPDFBlob,
} from "@shared/utils/utils";
import {
  assignDistributor,
  downloadBillTemplate,
  getAssignmentTypeList,
  getEmployeeList,
  getTerritoryConfigMap,
  getVendorList,
  PrintingDistributionApi,
} from "./api";
import { useState } from "react";
import { toast, useToast } from "@shared/ui/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";

export const useDistribution = (queryParams: Record<string, any>) => {
  const utilityId = getRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.distributionList(),
      utilityId,
      queryParams,
    ],
    () => PrintingDistributionApi.getDistribution(queryParams),
    { enabled: !!utilityId }
  );
};

export const useDistributionExport = (
  queryParams: Record<string, any> = {}
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.bx.printingDistribution.exportList(), // Adjust this query key based on your factory structure
    async () => {
      setIsExporting(true);
      try {
        const blob = await PrintingDistributionApi.exportDistribution(
          queryParams
        ); // Adjust API object name if needed

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export distribution data: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};
export const usePrintList = (queryParams: Record<string, any>) => {
  const utilityId = getRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.printList(),
      utilityId,
      queryParams,
    ],
    () => PrintingDistributionApi.getPrintData(queryParams),
    { enabled: !!utilityId }
  );
};

export const usePrintingExport = (queryParams: Record<string, any> = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.bx.printingDistribution.exportList(), // Adjust this query key based on your factory structure
    async () => {
      setIsExporting(true);
      try {
        const blob = await PrintingDistributionApi.exportPrintData(queryParams); // Adjust API object name if needed

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export printing cycles: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};
export const usePrintSummary = () => {
  const utilityId = getRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.printSummary(),
      utilityId,
    ],
    () => PrintingDistributionApi.getPrintSummary(utilityId),
    { enabled: !!utilityId }
  );
};
export const useConsumerList = (params: Record<string, any>) =>
  useSmartQuery(
    [...QueryKeyFactory.module.bx.printingDistribution.consumerList(), params],
    () => PrintingDistributionApi.getConsumerList(params),
    { enabled: !!params.bill_run_id }
  );

export const useConsumerListSummary = (billRunId: string | number) =>
  useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.consumerListSummary(),
      billRunId,
    ],
    () => PrintingDistributionApi.getConsumerListSummary(billRunId),
    { enabled: !!billRunId }
  );

export const usePaperBillSummary = (billRunId: string | number) =>
  useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.paperBillListSummary(),
      billRunId,
    ],
    () => PrintingDistributionApi.getPaperBillDistributionSummary(billRunId),
    { enabled: !!billRunId }
  );

export const usePaperBillList = (billRunId: string | number,searchQuery?:string) =>
  useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.paperBillList(searchQuery),
      billRunId,
    ],
    () => PrintingDistributionApi.getPaperBillDistribution(billRunId,searchQuery),
    { enabled: !!billRunId }
  );

import { useQueryClient } from "@tanstack/react-query";

export const useUpdatePrintStatus = () => {
  const queryClient = useQueryClient();

  return useSmartMutation<any, any, any>(
    async ({ runId, payload }) => {
      return PrintingDistributionApi.updatePrintStatus(runId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["bx", "billing", "details", "print-summary"],
        });
      },
    }
  );
};


export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await downloadBillTemplate(billId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const useViewBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    console.log("Blob received:", blob);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const usePrintBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    printPDFBlob(blob);
    return blob;
  });
};

export const useVendorList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.printingDistribution.vendorList(),
    () => getVendorList()
  );
};
export const useEmployeeList = (open: boolean) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.printingDistribution.employeeList(),
    () => getEmployeeList(),
    { enabled: open }
  );
};

export const useDistributionSummary = () => {
  const utilityId = getRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.printingDistribution.distributionSummary(),
      utilityId,
    ],
    () => PrintingDistributionApi.getDistributionSummary(utilityId),
    { enabled: !!utilityId }
  );
};

export const  useSubAreaList = (open: boolean) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.printingDistribution.subAreaList()],
    () => getTerritoryConfigMap("sub_area", remoteUtilityId),
    {
      enabled: !!remoteUtilityId || open,
    }
  );
};

export const useAssignmentTypeList = (open: boolean) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.printingDistribution.assignmentTypeList(),
    () => getAssignmentTypeList(),
    { enabled: open }
  );
};

export const useassignDistributor = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => assignDistributor(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Assignment Distributor",
          description: "Distribution assigned successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["bx", "billing", "list","paper-bill-list-details"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};
